import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2} from '@blueprint/ui';
import paymentApi from "../../../api/payment";
import { Loader, toast } from "../../../common/components/core";
import {
    useAuthErrorHandlerCreator,
    useModalWithData,
} from "../../../common/hooks";
import {
    composeErrorHandlers,
    GenericError,
    onStaleStateError,
    onBankStateError,
} from "../../../utils/errorHandling";
import InvoiceContext from "../../invoice/InvoiceContext";

type ApprovePaymentHandler = {
    isSubmitting: boolean;
    handler: (preSubmitPaymentLinearId: string) => void;
};

const useDirectorApprovePaymentHandler = (): ApprovePaymentHandler => {
    const [isSubmitting, setSubmitting] = useState(false);
    const { mutate } = useContext(InvoiceContext);
    const onAuthError = useAuthErrorHandlerCreator();
    const { handler: unableApproveHandler } = useUnableApproveHandler();

    const [show, hide] = useModalWithData<string>(
        (preSubmitPaymentLinearId) => () => {
            const confirm = async () => {
                if (!preSubmitPaymentLinearId) {
                    return;
                }
                setSubmitting(true);
                try {
                    const preSubmitPayment =
                        await paymentApi.getPreSubmitPayment(
                            preSubmitPaymentLinearId,
                        );
                    await paymentApi.directorApprove(preSubmitPayment.ref);
                    mutate();
                    toast("Payment order approved");
                    hide();
                } catch (e) {
                    composeErrorHandlers(
                        onBankStateError(() => {
                            hide();
                            unableApproveHandler("");
                        }),
                        onStaleStateError(() => {
                            mutate();
                            hide();
                            toast("Page has been updated by another user.");
                        }),
                        onAuthError,
                    )(e as GenericError);
                }
                setSubmitting(false);
            };

            return (
                <ThemeProviderV2>
                <Modal show onHide={hide} backdrop="static" centered>
                    <Loader overlay isLoading={isSubmitting}>
                        <Modal.Header>
                            <h2 className="mb-0">
                                Approve Payment Order
                            </h2>
                        </Modal.Header>
                        <Modal.Body>
                            <p>
                                Are you sure you want to approve this payment
                                order?
                            </p>
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-between px-3">
                            <Button variant="light.tertiary" onClick={hide}>
                                Cancel
                            </Button>
                            <Button variant="light.primary" onClick={confirm}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Loader>
                </Modal>
                </ThemeProviderV2>
            );
        },
        [onAuthError, isSubmitting],
    );

    return {
        isSubmitting,
        handler: show,
    };
};

const useUnableApproveHandler = () => {
    const onAuthError = useAuthErrorHandlerCreator();

    const [show, hide] = useModalWithData<string>(
        () => () => {

            return (
                <ThemeProviderV2>
                <Modal show onHide={hide} backdrop="static" centered>
                    <Loader overlay>
                        <Modal.Header>
                            <h2 className="mb-0">
                                Unable to approve this payment order
                            </h2>
                        </Modal.Header>
                        <Modal.Body>
                            <p>
                                Please double check and make sure the payer account is active.
                            </p>
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-between px-3">
                            <Button variant="light.tertiary" onClick={hide}>
                                Cancel
                            </Button>
                            <Button variant="light.primary" onClick={hide}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Loader>
                </Modal>
                </ThemeProviderV2>
            );
        },
        [onAuthError],
    );

    return {
        handler: show,
    };
};

export default useDirectorApprovePaymentHandler;
